import React, { useState, useRef, useEffect } from 'react';
import { Mic, Square, Play, Trash2, Upload, Loader2, Check } from 'lucide-react';
import { toast } from 'sonner';
import axios from 'axios';

const BACKEND_URL = process.env.REACT_APP_BACKEND_URL;
const API = `${BACKEND_URL}/api`;

const AudioRecorder = ({ 
  itemType, 
  itemId, 
  exampleIndex = null, // For grammar examples and situation phrases
  currentAudioUrl, 
  token, 
  onAudioSaved,
  compact = false // Compact mode for inline display
}) => {
  const [isRecording, setIsRecording] = useState(false);
  const [audioBlob, setAudioBlob] = useState(null);
  const [audioUrl, setAudioUrl] = useState(currentAudioUrl || null);
  const [isUploading, setIsUploading] = useState(false);
  const [isPlaying, setIsPlaying] = useState(false);
  
  const mediaRecorderRef = useRef(null);
  const audioChunksRef = useRef([]);
  const audioRef = useRef(null);

  // CRITICAL FIX: Update audioUrl when currentAudioUrl prop changes
  useEffect(() => {
    if (currentAudioUrl && currentAudioUrl !== audioUrl && !audioUrl?.startsWith('blob:')) {
      setAudioUrl(currentAudioUrl);
    }
  }, [currentAudioUrl]);

  const startRecording = async () => {
    try {
      const stream = await navigator.mediaDevices.getUserMedia({ audio: true });
      
      // Choose supported audio format - prefer MP4/AAC for Safari compatibility
      let mimeType = 'audio/webm';
      let fileExtension = 'webm';
      
      if (MediaRecorder.isTypeSupported('audio/mp4')) {
        mimeType = 'audio/mp4';
        fileExtension = 'm4a';
      } else if (MediaRecorder.isTypeSupported('audio/webm')) {
        mimeType = 'audio/webm';
        fileExtension = 'webm';
      } else if (MediaRecorder.isTypeSupported('audio/ogg')) {
        mimeType = 'audio/ogg';
        fileExtension = 'ogg';
      }
      // else use browser default
      
      const recorderOptions = MediaRecorder.isTypeSupported(mimeType) ? { mimeType } : {};
      mediaRecorderRef.current = new MediaRecorder(stream, recorderOptions);
      audioChunksRef.current = [];
      
      // Store the mime type and extension for later use
      mediaRecorderRef.current._mimeType = mimeType;
      mediaRecorderRef.current._fileExtension = fileExtension;

      mediaRecorderRef.current.ondataavailable = (event) => {
        if (event.data.size > 0) {
          audioChunksRef.current.push(event.data);
        }
      };

      mediaRecorderRef.current.onstop = () => {
        const actualMimeType = mediaRecorderRef.current._mimeType || 'audio/webm';
        const blob = new Blob(audioChunksRef.current, { type: actualMimeType });
        // Store extension on blob for upload
        blob._fileExtension = mediaRecorderRef.current._fileExtension || 'webm';
        setAudioBlob(blob);
        setAudioUrl(URL.createObjectURL(blob));
        
        // Stop all tracks
        stream.getTracks().forEach(track => track.stop());
      };

      mediaRecorderRef.current.start();
      setIsRecording(true);
      if (!compact) toast.info('Enregistrement en cours...');
    } catch (error) {
      console.error('Error accessing microphone:', error);
      toast.error('Impossible d\'accéder au microphone. Vérifiez les permissions.');
    }
  };

  const stopRecording = () => {
    if (mediaRecorderRef.current && isRecording) {
      mediaRecorderRef.current.stop();
      setIsRecording(false);
      if (!compact) toast.success('Enregistrement terminé !');
    }
  };

  const playAudio = () => {
    if (audioRef.current) {
      audioRef.current.play();
      setIsPlaying(true);
      audioRef.current.onended = () => setIsPlaying(false);
    }
  };

  const deleteRecording = () => {
    setAudioBlob(null);
    if (!currentAudioUrl) {
      setAudioUrl(null);
    } else {
      setAudioUrl(currentAudioUrl);
    }
  };

  const uploadAudio = async () => {
    if (!audioBlob) {
      toast.error('Aucun audio à enregistrer');
      return;
    }

    setIsUploading(true);
    try {
      const formData = new FormData();
      const fileExtension = audioBlob._fileExtension || 'webm';
      formData.append('file', audioBlob, `recording.${fileExtension}`);
      formData.append('item_type', itemType);
      formData.append('item_id', itemId);
      if (exampleIndex !== null) {
        formData.append('example_index', exampleIndex.toString());
      }

      const response = await axios.post(`${API}/admin/upload/audio`, formData, {
        headers: {
          'Authorization': `Bearer ${token}`,
          'Content-Type': 'multipart/form-data'
        }
      });

      setAudioUrl(response.data.audio_url);
      setAudioBlob(null);
      toast.success('Audio sauvegardé !');
      
      if (onAudioSaved) {
        onAudioSaved(response.data.audio_url);
      }
    } catch (error) {
      console.error('Upload error:', error);
      toast.error('Erreur lors de l\'upload de l\'audio');
    } finally {
      setIsUploading(false);
    }
  };

  const fullAudioUrl = audioUrl?.startsWith('blob:') ? audioUrl : (audioUrl ? `${BACKEND_URL}${audioUrl}` : null);

  // Compact mode for inline use in tables
  if (compact) {
    const hasSavedAudio = audioUrl && !audioUrl.startsWith('blob:');
    
    return (
      <div className={`flex items-center gap-1 px-2 py-1 rounded-lg ${
        hasSavedAudio ? 'bg-green-50 border border-green-200' : 'bg-red-50 border border-red-200'
      }`}>
        {fullAudioUrl && (
          <audio ref={audioRef} src={fullAudioUrl} />
        )}

        {!isRecording ? (
          <button
            onClick={startRecording}
            type="button"
            className={`p-1.5 rounded-full transition-colors ${
              hasSavedAudio 
                ? 'bg-green-500 hover:bg-green-600 text-white' 
                : 'bg-red-500 hover:bg-red-600 text-white'
            }`}
            title={hasSavedAudio ? "Ré-enregistrer" : "Enregistrer"}
          >
            <Mic className="w-3 h-3" />
          </button>
        ) : (
          <button
            onClick={stopRecording}
            type="button"
            className="p-1.5 bg-red-600 text-white rounded-full animate-pulse"
            title="Stop"
          >
            <Square className="w-3 h-3" />
          </button>
        )}

        {audioUrl && !audioBlob && (
          <button
            onClick={playAudio}
            type="button"
            className={`p-1.5 rounded-full transition-colors ${
              isPlaying ? 'bg-green-600 text-white' : 'bg-green-500 hover:bg-green-600 text-white'
            }`}
            title="Écouter"
          >
            <Play className="w-3 h-3" />
          </button>
        )}

        {audioBlob && (
          <>
            <button
              onClick={playAudio}
              type="button"
              className="p-1.5 bg-green-500 hover:bg-green-600 text-white rounded-full"
              title="Écouter"
            >
              <Play className="w-3 h-3" />
            </button>
            <button
              onClick={uploadAudio}
              type="button"
              disabled={isUploading}
              className="p-1.5 bg-primary hover:bg-primary/90 text-white rounded-full"
              title="Sauvegarder"
            >
              {isUploading ? <Loader2 className="w-3 h-3 animate-spin" /> : <Upload className="w-3 h-3" />}
            </button>
            <button
              onClick={deleteRecording}
              type="button"
              className="p-1.5 bg-stone-400 hover:bg-stone-500 text-white rounded-full"
              title="Annuler"
            >
              <Trash2 className="w-3 h-3" />
            </button>
          </>
        )}

        {/* Status indicator */}
        <span className={`text-xs font-nunito font-semibold ${
          hasSavedAudio ? 'text-green-600' : 'text-red-500'
        }`}>
          {hasSavedAudio ? '✓' : '○'}
        </span>
      </div>
    );
  }

  // Full mode (original)
  return (
    <div className="flex items-center gap-2 p-2 bg-stone-50 rounded-lg border border-stone-200">
      {fullAudioUrl && (
        <audio ref={audioRef} src={fullAudioUrl} />
      )}

      {!isRecording ? (
        <button
          onClick={startRecording}
          type="button"
          className="p-2 bg-red-500 hover:bg-red-600 text-white rounded-full transition-colors"
          title="Commencer l'enregistrement"
        >
          <Mic className="w-4 h-4" />
        </button>
      ) : (
        <button
          onClick={stopRecording}
          type="button"
          className="p-2 bg-red-600 hover:bg-red-700 text-white rounded-full animate-pulse transition-colors"
          title="Arrêter l'enregistrement"
        >
          <Square className="w-4 h-4" />
        </button>
      )}

      {audioUrl && (
        <button
          onClick={playAudio}
          type="button"
          disabled={isPlaying}
          className={`p-2 rounded-full transition-colors ${
            isPlaying 
              ? 'bg-green-600 text-white' 
              : 'bg-green-500 hover:bg-green-600 text-white'
          }`}
          title="Écouter"
        >
          <Play className="w-4 h-4" />
        </button>
      )}

      {audioBlob && (
        <button
          onClick={deleteRecording}
          type="button"
          className="p-2 bg-stone-400 hover:bg-stone-500 text-white rounded-full transition-colors"
          title="Supprimer"
        >
          <Trash2 className="w-4 h-4" />
        </button>
      )}

      {audioBlob && (
        <button
          onClick={uploadAudio}
          type="button"
          disabled={isUploading}
          className="p-2 bg-primary hover:bg-primary/90 text-white rounded-full transition-colors flex items-center gap-1"
          title="Sauvegarder l'audio"
        >
          {isUploading ? (
            <Loader2 className="w-4 h-4 animate-spin" />
          ) : (
            <Upload className="w-4 h-4" />
          )}
        </button>
      )}

      <span className="text-xs text-charcoal/60 font-nunito ml-2">
        {isRecording && '🔴 Enreg...'}
        {!isRecording && audioUrl && !audioBlob && '✅'}
        {!isRecording && audioBlob && '⏳ À sauv.'}
        {!isRecording && !audioUrl && !audioBlob && 'Pas d\'audio'}
      </span>
    </div>
  );
};

export default AudioRecorder;
